'use client';
import type { ComponentPropsWithoutRef } from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { FileUploader as BaseFileUploader } from '@/slw/shared/ui/file-uploader';
import { cn } from '@/slw/shared/lib/utils';

// ----------------------------------------------------------------------

interface CustomRHFInputProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseFileUploader>, 'name' | 'defaultValue'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  withError?: boolean;
  isLoading?: boolean;
  showMessError?: boolean;
  direction?: 'row' | 'column';
  className?: string;
  onUpload?: (files: File[]) => Promise<void>;
  isPreview?: boolean;
}

export function FileUploader<TFieldValues extends FieldValues>({
  name,
  label,
  helperText,
  defaultValue,
  containerClass,
  progresses,
  withError = true,
  showMessError = true,
  disabled,
  isLoading,
  direction = 'row',
  className,
  isPreview = false,
  onUpload,
  ...other
}: CustomRHFInputProps<TFieldValues>) {
  const { control } = useFormContext();
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <FormItem className={cn(directionClass, containerClass)}>
          {!!label && <FormLabel className={cn('whitespace-nowrap')}>{label}</FormLabel>}
          <div className='flex-1 w-full'>
            <FormControl>
              <BaseFileUploader
                className={cn(className, error && withError && 'border-destructive focus-visible:ring-destructive')}
                value={field.value}
                onValueChange={field.onChange}
                onUpload={onUpload}
                isPreview={isPreview}
                progresses={progresses}
                disabled={isLoading || disabled}
                {...other}
              />
            </FormControl>
            {helperText && withError && showMessError && (
              <p className='text-sm text-muted-foreground ml-1.5'>{helperText}</p>
            )}
            {error && withError && showMessError && <FormMessage>{error.message}</FormMessage>}
          </div>
        </FormItem>
      )}
    />
  );
}
